import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { route } from "../../../../globals/constants";
import FastImage from "../../../../globals/elements/fastimg";
import { NavLink } from "react-router-dom";

const SECTION_TITLE = "News & Insights";
const SECTION_INTRO =
    "Stay updated with the latest developments, industry trends, and company highlights from EZ Storage and Warehousing — your trusted 3PL partner in Kuwait.";

const mapBlogToCard = (blog) => {
    if (!blog) {
        return null;
    }

    const monthNames = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
    const dateSource = blog.publishedDate || blog.createdAt;
    let day = "";
    let monthLabel = "";
    let fullDate = "";

    if (typeof dateSource === "string" && /^\d{2}\/\d{2}\/\d{2}$/.test(dateSource)) {
        const [dd, mm, yy] = dateSource.split("/");
        day = dd;
        const monthIndex = Number(mm) - 1;
        monthLabel = monthNames[monthIndex] || "";
        fullDate = `${dd} ${monthLabel.toUpperCase()} 20${yy}`;
    } else if (dateSource) {
        const parsed = new Date(dateSource);
        if (!Number.isNaN(parsed.getTime())) {
            day = String(parsed.getDate()).padStart(2, "0");
            const monthIndex = parsed.getMonth();
            monthLabel = monthNames[monthIndex] || "";
            fullDate = `${day} ${monthLabel.toUpperCase()} ${parsed.getFullYear()}`;
        }
    }

    return {
        id: blog._id,
        title: blog.title || "Untitled blog post",
        description: blog.description || "",
        excerpt: blog.description || "",
        image: blog.image || "/images/blog/default-blog.jpg",
        category: blog.category || "General",
        day,
        date: fullDate || "",
        author: blog.author || "Admin"
    };
};

const fetchBlogs = async () => {
    const API_URL = process.env.REACT_APP_API_URL || "";
    const response = await fetch(`${API_URL}/api/blogs`);
    
    if (!response.ok) {
        let errorMessage = "Failed to load blog posts.";
        try {
            const data = await response.json();
            if (data?.error) {
                errorMessage = data.error;
            }
        } catch {
            // ignore parse errors
        }
        throw new Error(errorMessage);
    }
    
    const data = await response.json();
    return Array.isArray(data?.items) ? data.items : [];
};

function SectionHome1Blogs() {
    // Use React Query for automatic caching and background refetching
    const { data: blogs = [], isLoading, error } = useQuery({
        queryKey: ['blogs'],
        queryFn: fetchBlogs,
        staleTime: 10 * 60 * 1000, // Keep data fresh for 10 minutes
    });

    const cards = useMemo(() => {
        return blogs.slice(0, 3).map(mapBlogToCard).filter(Boolean);
    }, [blogs]);

    return (
        <div className="section-full  p-t80 p-b50 bg-white sx-news-blog-1">
            <div className="container">
                {/* TITLE START*/}
                <div className="section-head text-center">
                    <h2 data-title="Blog">{SECTION_TITLE}</h2>
                    <p className="m-b30">{SECTION_INTRO}</p>
                    <div className="mt-separator-outer">
                        <div className="mt-separator site-bg-primary" />
                    </div>
                </div>
                {/* TITLE END*/}
                <div className="section-content ">
                    <div className="row">
                        {isLoading ? (
                            <div className="col-12">
                                <p className="text-center">Loading latest blog posts…</p>
                            </div>
                        ) : error ? (
                            <div className="col-12">
                                <div className="alert alert-danger text-center" role="alert">
                                    {error?.message || "Unable to load blog posts."}
                                </div>
                            </div>
                        ) : cards.length === 0 ? (
                            <div className="col-12">
                                <p className="text-center">No blog posts available yet. Check back soon!</p>
                            </div>
                        ) : (
                            cards.map((post, index) => {
                                const blogDetailUrl = post.id ? route.blog.getDetailUrl(post.id) : route.blog.POST_SINGLE;
                                return (
                                    <div key={post.id || index} className="col-lg-4 col-md-6">
                                        <div className="blog-post latest-blog-1 date-style-2 overflow-hide bg-white bdr-1 bdr-solid bdr-gray-light m-b30">
                                            <div className="mt-post-media">
                                                <NavLink to={blogDetailUrl}>
                                                    <FastImage src={post.image} alt={post.title} />
                                                </NavLink>
                                            </div>
                                            <div className="mt-post-info p-a30">
                                                <div className="mt-post-meta text-black bdr-1 bdr-solid bdr-b-1 bdr-gray-light p-b20">
                                                    <ul>
                                                        {(post.day || post.date) && (
                                                            <li className="post-date">
                                                                <i className="fa fa-calendar site-text-primary" />
                                                                {post.day && <strong>{post.day}</strong>}
                                                                {post.date && <span>{post.date}</span>}
                                                            </li>
                                                        )}
                                                        <li className="post-author">
                                                            <i className="fa fa-user site-text-primary" />
                                                            By <span>{post.author}</span>
                                                        </li>
                                                        {post.category && (
                                                            <li className="post-comment">
                                                                <i className="fa fa-comments site-text-primary" />
                                                                <NavLink to={route.blog.LIST}>{post.category}</NavLink>
                                                            </li>
                                                        )}
                                                    </ul>
                                                </div>
                                                <div className="mt-post-title">
                                                    <h4 className="post-title">
                                                        <NavLink to={blogDetailUrl}>{post.title}</NavLink>
                                                    </h4>
                                                </div>
                                                <div className="mt-post-text">
                                                    <p>{post.excerpt}</p>
                                                </div>
                                                <NavLink to={blogDetailUrl} className="site-button-link">Read More</NavLink>
                                            </div>
                                        </div>
                                    </div>
                                );
                            })
                        )}
                    </div>
                </div>
            </div>
        </div>
    )
}

export default SectionHome1Blogs;
